<template>
  <!-- La raíz queda en su lugar; el popup se muda a <body> (ver DashboardWidgetCatalog). -->
  <span class="dpd-anchor">
    <div v-show="value" ref="overlay" class="dpd" @keydown.esc="close">
      <div class="dpd__backdrop" @click="close"></div>
      <div class="dpd__panel" role="dialog" aria-modal="true" :aria-label="heading">
        <header class="dpd__head">
          <div class="dpd__titles">
            <h2 class="dpd__title">{{ heading }}</h2>
            <p class="dpd__subtitle">{{ isEdit ? metaOf(preset) : t("createSubtitle") }}</p>
          </div>
          <button type="button" class="dpd__close" :aria-label="t('close')" @click="close">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </header>

        <form class="dpd__body" @submit.prevent="save">
          <label class="dpd__field">
            <span>{{ t("name") }}</span>
            <input v-model.trim="form.name" type="text" class="form-control form-control-sm" maxlength="120" :placeholder="t('namePlaceholder')" />
          </label>
          <label class="dpd__field">
            <span>{{ t("description") }}</span>
            <input v-model="form.description" type="text" class="form-control form-control-sm" maxlength="500" :placeholder="t('descriptionPlaceholder')" />
          </label>
          <label class="dpd__switch">
            <input v-model="form.isPublic" type="checkbox" role="switch" />
            <span>{{ t("share") }}</span>
          </label>
          <p v-if="form.isPublic" class="dpd__hint">{{ t("shareHint") }}</p>
          <!-- Enter en un campo guarda (el botón visible está en el pie, fuera del form). -->
          <button type="submit" class="d-none" tabindex="-1" aria-hidden="true"></button>

          <template v-if="isEdit">
            <div class="dpd__actions">
              <button type="button" class="dpd__action" @click="overwrite">
                <i class="fas fa-sync-alt" aria-hidden="true"></i>
                <span>
                  <strong>{{ t("overwrite") }}</strong>
                  <small>{{ t("overwriteHint") }}</small>
                </span>
              </button>
              <button v-if="isAdmin" type="button" class="dpd__action" @click="applyToAll">
                <i class="fas fa-users-cog" aria-hidden="true"></i>
                <span>
                  <strong>{{ t("applyToAll") }}</strong>
                  <small>{{ t("applyToAllHint") }}</small>
                </span>
              </button>
              <button v-if="isAdmin && preset.isDefault" type="button" class="dpd__action" @click="clearDefault">
                <i class="far fa-star" aria-hidden="true"></i>
                <span>
                  <strong>{{ t("clearDefault") }}</strong>
                  <small>{{ t("clearDefaultHint") }}</small>
                </span>
              </button>
              <button type="button" class="dpd__action dpd__action--danger" @click="remove">
                <i class="fas fa-trash-alt" aria-hidden="true"></i>
                <span>
                  <strong>{{ t("delete") }}</strong>
                  <small v-if="preset.isPublic">{{ t("deleteSharedText") }}</small>
                </span>
              </button>
            </div>
          </template>
        </form>

        <footer class="dpd__foot">
          <button type="button" class="btn btn-sm btn-link text-secondary" @click="close">{{ t("cancel") }}</button>
          <button type="button" class="btn btn-sm btn-primary" :disabled="!form.name || busy" @click="save">
            {{ isEdit ? t("saveChanges") : t("save") }}
          </button>
        </footer>
      </div>
    </div>
  </span>
</template>

<script>
/**
 * Popup de un dashboard guardado: crearlo a partir del actual o editar solo ese
 * (nombre, compartir, reemplazarlo con el actual, borrarlo y, el admin, aplicarlo a
 * todos). Va en el idioma de avisos ($tn), como el resto de la configuración.
 */
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import { formatTimestampSync } from "@/utils/dateFormatter";

export default {
  name: "DashboardPresetDialog",
  props: {
    value: { type: Boolean, default: false },
    /** null = crear uno nuevo con el dashboard actual. */
    preset: { type: Object, default: null },
    currentWidgets: { type: Array, default: () => [] },
    isAdmin: { type: Boolean, default: false },
    /** Al crear: nombre propuesto (p. ej. «X (copia)» al guardar uno ajeno como propio). */
    suggestedName: { type: String, default: "" },
  },
  data() {
    return { form: { name: "", description: "", isPublic: false }, busy: false };
  },
  computed: {
    isEdit() {
      return !!this.preset;
    },
    heading() {
      return this.isEdit ? this.t("editTitle", { name: this.preset.name }) : this.t("saveCurrent");
    },
  },
  watch: {
    value(open) {
      document.body.classList.toggle("dwc-open", !!open);
      if (open) {
        const p = this.preset;
        this.form = p
          ? { name: p.name, description: p.description || "", isPublic: !!p.isPublic }
          : { name: this.suggestedName || "", description: "", isPublic: false };
      }
    },
  },
  mounted() {
    document.body.appendChild(this.$refs.overlay);
  },
  beforeDestroy() {
    if (this.value) document.body.classList.remove("dwc-open");
    const el = this.$refs.overlay;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  },
  methods: {
    t(key, params) {
      return this.$tn(`dashboardWidgets.presets.${key}`, params);
    },
    close() {
      this.$emit("input", false);
    },
    metaOf(p) {
      if (!p) return "";
      const parts = [];
      if (!p.mine && p.createdBy && p.createdBy.name) parts.push(this.t("by", { name: p.createdBy.name }));
      parts.push(this.t("widgetsCount", { n: (p.widgets || []).length }));
      if (p.updatedAt) parts.push(formatTimestampSync(p.updatedAt));
      return parts.join(" · ");
    },
    payloadWidgets() {
      return this.currentWidgets.map(({ i, type, x, y, w, h, config }) => ({ i, type, x, y, w, h, config: config || {} }));
    },
    async save() {
      if (!this.form.name || this.busy) return;
      this.busy = true;
      try {
        if (this.isEdit) {
          const { data } = await wencesApi.put(`/api/users/dashboards/${this.preset._id}`, { ...this.form });
          this.$emit("saved", data && data.data);
        } else {
          const { data } = await wencesApi.post("/api/users/dashboards", { ...this.form, widgets: this.payloadWidgets() });
          this.$emit("created", data && data.data);
        }
        this.close();
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      } finally {
        this.busy = false;
      }
    },
    async overwrite() {
      const { isConfirmed } = await Swal.fire({
        title: this.t("overwriteTitle", { name: this.preset.name }),
        text: this.t("overwriteText"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.t("overwrite"),
        cancelButtonText: this.t("cancel"),
      });
      if (!isConfirmed) return;
      try {
        const { data } = await wencesApi.put(`/api/users/dashboards/${this.preset._id}`, { ...this.form, widgets: this.payloadWidgets() });
        this.$emit("overwritten", data && data.data);
        this.close();
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      }
    },
    async applyToAll() {
      const { isConfirmed } = await Swal.fire({
        title: this.t("applyToAllTitle", { name: this.preset.name }),
        text: this.t("applyToAllText"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.t("applyToAll"),
        cancelButtonText: this.t("cancel"),
      });
      if (!isConfirmed) return;
      try {
        const { data } = await wencesApi.post(`/api/users/dashboards/${this.preset._id}/apply-to-all`);
        this.$emit("applied-to-all");
        this.close();
        Swal.fire({ icon: "success", title: this.t("appliedToAll", { n: (data && data.data && data.data.users) || 0 }) });
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      }
    },
    async clearDefault() {
      try {
        await wencesApi.post("/api/users/dashboards/default/clear");
        this.$emit("changed");
        this.close();
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("saveError") });
      }
    },
    async remove() {
      const { isConfirmed } = await Swal.fire({
        title: this.t("deleteTitle", { name: this.preset.name }),
        text: this.preset.isPublic ? this.t("deleteSharedText") : "",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.t("delete"),
        cancelButtonText: this.t("cancel"),
      });
      if (!isConfirmed) return;
      try {
        await wencesApi.delete(`/api/users/dashboards/${this.preset._id}`);
        this.$emit("deleted", this.preset._id);
        this.close();
      } catch (e) {
        Swal.fire({ icon: "error", title: this.t("deleteError") });
      }
    },
  },
};
</script>

<style scoped>
.dpd-anchor { display: contents; }
.dpd { position: fixed; inset: 0; z-index: 1060; display: flex; align-items: center; justify-content: center; padding: 24px; }
.dpd__backdrop { position: absolute; inset: 0; background: rgba(var(--bs-black-rgb), 0.45); }
.dpd__panel {
  position: relative;
  width: min(520px, 100%);
  max-height: min(760px, 100%);
  display: flex;
  flex-direction: column;
  background: var(--dw-surface);
  color: var(--dw-text);
  border-radius: 16px;
  box-shadow: var(--dw-shadow-lg);
  overflow: hidden;
}
.dpd__head { display: flex; align-items: flex-start; gap: 12px; padding: 16px 20px 10px; border-bottom: 1px solid var(--dw-border); }
.dpd__titles { flex: 1; min-width: 0; }
.dpd__title { margin: 0; font-size: 1.05rem; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dpd__subtitle { margin: 2px 0 0; font-size: 0.82rem; color: var(--dw-muted); }
.dpd__close { border: 0; background: transparent; color: var(--dw-muted); width: 36px; height: 36px; border-radius: 50%; cursor: pointer; }
.dpd__close:hover { background: var(--dw-surface-2); color: var(--dw-text); }
.dpd__body { flex: 1; min-height: 0; overflow-y: auto; padding: 14px 20px; display: flex; flex-direction: column; gap: 10px; }
.dpd__field { display: flex; flex-direction: column; gap: 4px; font-size: 0.78rem; color: var(--dw-muted); margin: 0; }
.dpd__field .form-control { background: var(--dw-surface-2); color: var(--dw-text); border-color: var(--dw-border); }
.dpd__switch { display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; margin: 2px 0 0; cursor: pointer; }
.dpd__switch input { accent-color: var(--bs-primary); width: 16px; height: 16px; }
.dpd__hint { margin: 0; font-size: 0.75rem; color: var(--dw-muted); }
.dpd__actions { display: flex; flex-direction: column; gap: 6px; margin-top: 6px; padding-top: 12px; border-top: 1px solid var(--dw-border); }
.dpd__action {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  border: 1px solid var(--dw-border);
  background: var(--dw-surface);
  color: var(--dw-text);
  border-radius: 10px;
  padding: 9px 12px;
  cursor: pointer;
}
.dpd__action:hover { background: var(--dw-surface-2); }
.dpd__action i { color: var(--bs-primary); margin-top: 3px; width: 16px; text-align: center; }
.dpd__action span { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.dpd__action strong { font-size: 0.85rem; font-weight: 600; }
.dpd__action small { font-size: 0.74rem; color: var(--dw-muted); }
.dpd__action--danger i, .dpd__action--danger strong { color: var(--bs-danger); }
.dpd__foot { display: flex; justify-content: flex-end; gap: 8px; padding: 10px 20px; border-top: 1px solid var(--dw-border); }

@media (max-width: 767.98px) {
  .dpd { padding: 0; align-items: flex-end; }
  .dpd__panel { width: 100%; max-height: 92vh; border-radius: 16px 16px 0 0; }
  .dpd__foot { padding: 10px 16px calc(10px + env(safe-area-inset-bottom)); }
}
</style>
